import React, { useEffect, useMemo, useState } from "react";
import styles from "./BeveragesDialog.module.css";

import { createPortal } from "react-dom";
import { beverageCategories } from "./constants/beverage-categories";
import { beveragePreferences } from "./constants/beverage-preferences";
import { MorningIcon } from "../../components/Icon/Icons/MorningIcon";
import { AfternoonIcon } from "../../components/Icon/Icons/AfternoonIcon";
import { EveningIcon } from "../../components/Icon/Icons/EveningIcon";
import { AnytimeIcon } from "../../components/Icon/Icons/AnytimeIcon";
import { isBeverageDataValid } from "./utils";
import { useMatchBreakpoint } from "../../hooks/useMatchBreakpoint";
import { beveragePlaceholders } from "./constants/beverage-placeholders";
import { MAX_BEVERAGE_DESCRIPTION_LENGTH } from "../../utils/beverageDescriptionLimit";
import { CharacterCount, InvalidCharactersError } from "~/components";
import { hasDisallowedCharacters } from "../../utils/beverageTextValidation";

const timeOfDayOptions = [
  { name: "Morning", Icon: MorningIcon },
  { name: "Afternoon", Icon: AfternoonIcon },
  { name: "Evening", Icon: EveningIcon },
  { name: "Anytime", Icon: AnytimeIcon },
];

export const BeveragesDetailsDialog = (props) => {
  const { onClose, categoriesDetails, setNewDetails, onBack, onSave, isSaving } = props;
  const [details, setDetails] = useState({});

  const isInMobilePanel = useMatchBreakpoint(768);

  useEffect(() => {
    setDetails(structuredClone(categoriesDetails));
  }, [categoriesDetails]);

  const isSubmitEnabled = useMemo(() => {
    const hasInvalidText = Object.values(details).some((detail) =>
      hasDisallowedCharacters(detail.additionalDetails),
    );
    return isBeverageDataValid(details) && !hasInvalidText;
  }, [details]);

  const categories = Object.keys(details);

  const handleOverlayClick = (e) => {
    if (e.target.className === styles.overlay) {
      onClose();
    }
  };

  const updateDetails = (category, text) => {
    setDetails(old => ({
      ...old,
      [category]: {
        ...old[category],
        additionalDetails: text
      },
    }));
  };

  const togglePreference = (category, pref) => {
    setDetails(old => ({
      ...old,
      [category]: {
        ...old[category],
        types: old[category].types.includes(pref) ? old[category].types.filter((c) => c !== pref) : [...old[category].types, pref]
      },
    }))
  };

  const toggleTimeOfDay = (category, timeOfDay) => {
    setDetails(old => {
      const categoryTimes = old[category].timeOfDay;
      const isSelected = categoryTimes.includes(timeOfDay);
      let newValue = isSelected
        ? categoryTimes.filter((i) => i !== timeOfDay)
        : [...categoryTimes, timeOfDay];

      // We want to toggle items properly, so we will select anytime OR other options. Statements below are responsible for that
      if (timeOfDay === "Anytime") {
        newValue = newValue.filter((i) => i === timeOfDay);
      } else {
        newValue = newValue.filter((i) => i !== "Anytime");
      }

      if (newValue.length === 3) {
        newValue = ["Anytime"];
      }


      return {
        ...old,
        [category]: {
          ...old[category],
          timeOfDay: newValue
        },
      };
    })
  };
  
  const renderTextarea = (category) => {
    return (
      <div className={styles.textAreaWrapper}>
        <label htmlFor={`${category.name}-details`} className={styles.label}>
          Details (optional)
        </label>
        <textarea
          data-testid="beverage-details-input"
          placeholder={beveragePlaceholders[category.name]}
          id={`${category.name}-details`}
          className={styles.textArea}
          onChange={(e) => updateDetails(category.name, e.target.value)}
          value={details[category.name].additionalDetails}
          maxLength={MAX_BEVERAGE_DESCRIPTION_LENGTH}
        ></textarea>
        <CharacterCount
          value={details[category.name].additionalDetails}
          max={MAX_BEVERAGE_DESCRIPTION_LENGTH}
        />
        <InvalidCharactersError value={details[category.name].additionalDetails} />
      </div>
    );
  };

  return createPortal(
    <div className={styles.overlay} data-testid="beverage-details-dialog-overlay" onClick={handleOverlayClick}>
      <div className={styles.dialog} data-testid="beverage-details-dialog" role="dialog" aria-modal="true">
        <button
          data-testid="beverage-details-close"
          className={`${styles.closeButton} custom_click_track`}
          data-custom_click_track_value="Beverages|Close Customize Modal|internal"
          onClick={onClose}
          aria-label="Close beverage details"
        >
          ✕
        </button>

        <button
          data-testid="beverage-details-back"
          aria-label="Back to beverage categories"
          className={`${styles.back} custom_click_track`}
          data-custom_click_track_value="Beverages|Back to Categories|internal"
          onClick={onBack}
        >
          &#8618;
        </button>

        <h2>How do you like your ...</h2>

        <p className={styles.hint}>
          Please do not include profanity, unintelligible information,
          personally identifiable information, or offensive or inappropriate
          content.
        </p>

        <ul className={styles.categoriesDetails}>
          {categories.map((c) => {
            const category = beverageCategories.find(({ name }) => name === c);

            return (
              <li key={category.name} data-testid="beverage-category-details">
                <div
                  className={`${styles.category} ${styles.selectedCategory} ${styles.noSelectable}`}
                  key={category.name}
                >
                  <img src={category.activeIcon} alt={category.name} />
                  <p>{category.name}</p>
                </div>

                <div></div>
                <div className={styles.timeTypeDetails}>
                  <label className={styles.label}>Type</label>

                  <div className={styles.typeDetails}>
                    {beveragePreferences[category.name].map(
                      (preference, index) => {
                        const isSelected =
                          details[category.name].types.includes(preference);
                        return (
                          <button
                            key={index}
                            data-testid="beverage-type-option"
                            aria-pressed={isSelected}
                            className={`${styles.optionButton} ${isSelected ? styles.optionButtonSelected : ""} custom_click_track`}
                            data-custom_click_track_value={`Beverages|Selected Category: ${category.name}, Preference Selection: ${preference}|internal`}
                            onClick={() =>
                              togglePreference(category.name, preference)
                            }
                          >
                            {preference}
                          </button>
                        );
                      },
                    )}
                  </div>

                  {isInMobilePanel && renderTextarea(category)}

                  <label className={styles.label}>Time of Day (optional)</label>

                  <div className={styles.timeOfDayGrid}>
                    {timeOfDayOptions.map(({ name, Icon }) => {
                      const isSelected =
                        details[category.name].timeOfDay.includes(name);
                      return (
                        <button
                          key={name}
                          data-testid="beverage-time-option"
                          aria-pressed={isSelected}
                          className={`${styles.timeOfDayButton} ${isSelected ? styles.timeOfDayButtonSelected : ""} custom_click_track`}
                          data-custom_click_track_value={`Beverages|Selected Category: ${category.name}, Time Of Day Selection: ${name}|internal`}
                          onClick={() => toggleTimeOfDay(category.name, name)}
                        >
                          <Icon alt={name} className={styles.timeOfDayIcon} />
                          <span className={styles.timeOfDayLabel}>{name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {!isInMobilePanel && renderTextarea(category)}
              </li>
            );
          })}
        </ul>

        <div className={styles.buttonArea}>
          <button
            data-testid="beverage-details-save"
            onClick={() => {
              setNewDetails(details);
              if (onSave) {
                onSave(details);
              } else {
                onClose();
              }
            }}
            className={`${styles.primary} custom_click_track`}
            data-custom_click_track_value={`Beverages|Save Category Preferences: ${categories.length === 1 ? categories[0] : "MULTI"}|internal`}
            disabled={!isSubmitEnabled || isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}